import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, AlertTriangle, Check } from "lucide-react";
import { api } from "../../lib/api";
import { usePageMeta } from "../../components/Layout";

const Row = ({ children, testid }) => (
    <div className="bg-white border border-[#E5E2DC] rounded-md p-3 flex items-start gap-3" data-testid={testid}>{children}</div>
);

export function ProjectClientActions() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({});
    const [creating, setCreating] = useState(false);
    const [title, setTitle] = useState("");
    const [instr, setInstr] = useState("");
    const [dueAt, setDueAt] = useState("");
    const [editingRow, setEditingRow] = useState(null);
    const [editDueAt, setEditDueAt] = useState("");
    usePageMeta({ title: "Client actions · Project | Studio G4 Admin" });
    const load = () => api.get(`/admin/projects/${id}/client-actions`).then(r => {
        setRows(r.data?.items || []); setSummary(r.data?.summary || {});
    });
    useEffect(() => { load(); }, [id]);
    const submit = async () => {
        try {
            await api.post(`/admin/projects/${id}/client-actions`, {
                action_type: "general_client_task", title,
                client_instructions: instr,
                due_at: dueAt ? new Date(dueAt).toISOString() : null,
                create_op_id: `ca-${Date.now()}`,
            });
            setCreating(false); setTitle(""); setInstr(""); setDueAt("");
            load();
        } catch (e) { alert("Create failed: " + (e?.response?.data?.detail || e.message)); }
    };
    const submitDueAtEdit = async () => {
        if (!editingRow) return;
        try {
            const iso = editDueAt ? new Date(editDueAt).toISOString() : null;
            await api.patch(
                `/admin/projects/${id}/client-actions/${editingRow.id}/due-at`,
                {
                    expected_version: editingRow.record_version || 0,
                    due_at: iso,
                    operation_id: `ca-due-${editingRow.id}-${Date.now()}`,
                },
            );
            setEditingRow(null); setEditDueAt(""); load();
        } catch (e) { alert("Update failed: " + (e?.response?.data?.detail || e.message)); }
    };
    return (
        <div className="flex flex-col gap-4" data-testid="client-actions-page">
            <button onClick={() => navigate(`/admin/projects/${id}`)}
                data-testid="ca-back" className="self-start inline-flex items-center gap-1.5 text-[13px] text-[#666] hover:text-charcoal">
                <ArrowLeft className="w-4 h-4" /> Back to workspace
            </button>
            <div className="flex items-center justify-between">
                <h1 className="font-manrope text-2xl font-semibold text-charcoal">Client Actions</h1>
                <button onClick={() => setCreating(true)} data-testid="ca-new"
                    className="inline-flex items-center gap-1.5 text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md">
                    <Plus className="w-4 h-4" /> New action
                </button>
            </div>
            <div className="grid grid-cols-4 gap-3">
                {[["Active", summary.active], ["Overdue", summary.overdue],
                  ["Required", summary.required], ["Completed", summary.completed]].map(([l, v]) => (
                    <div key={l} className="bg-white border border-[#E5E2DC] rounded-md p-3">
                        <div className="text-[11px] uppercase font-manrope text-[#666]">{l}</div>
                        <div className="font-manrope text-2xl font-semibold">{v ?? 0}</div>
                    </div>
                ))}
            </div>
            <div className="flex flex-col gap-2">
                {rows.length === 0 && <div className="text-[13px] text-[#666]">No client actions yet.</div>}
                {rows.map(r => (
                    <Row key={r.id} testid={`ca-row-${r.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="font-manrope font-semibold text-[14px]">{r.title}</div>
                            <div className="text-[12px] text-[#666]">
                                {r.action_type} · {r.status} · origin {r.origin}
                                {r.due_at ? ` · due ${r.due_at.slice(0, 10)}` : ""}
                                {r.overdue ? " · OVERDUE" : ""}
                            </div>
                            {r.workflow_owned && (
                                <div className="text-[11px] text-terracotta mt-0.5" data-testid={`ca-workflow-owned-${r.id}`}>
                                    Workflow-owned — edits managed by {r.linked_source_type}
                                </div>
                            )}
                        </div>
                        {!r.workflow_owned && (r.status === "Active" || r.status === "Draft") && (
                            <button
                                onClick={() => {
                                    setEditingRow(r);
                                    setEditDueAt(r.due_at ? r.due_at.slice(0, 16) : "");
                                }}
                                data-testid={`ca-edit-due-${r.id}`}
                                className="text-[12px] text-[#A8543F] underline">
                                Edit due date
                            </button>
                        )}
                    </Row>
                ))}
            </div>
            {creating && (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40" data-testid="ca-create-modal">
                    <div className="bg-white rounded-lg p-4 w-[420px] flex flex-col gap-3">
                        <div className="font-manrope font-semibold">New client action</div>
                        <input value={title} onChange={e => setTitle(e.target.value)}
                            placeholder="Title" data-testid="ca-new-title"
                            className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                        <textarea value={instr} onChange={e => setInstr(e.target.value)}
                            placeholder="Instructions" rows={4} data-testid="ca-new-instructions"
                            className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                        <label className="flex flex-col gap-1">
                            <span className="text-[11px] uppercase font-manrope text-[#666]">Due date &amp; time (optional)</span>
                            <input type="datetime-local" value={dueAt}
                                onChange={e => setDueAt(e.target.value)}
                                data-testid="ca-new-due-at"
                                className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                        </label>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => { setCreating(false); setDueAt(""); }} className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md">Cancel</button>
                            <button onClick={submit} data-testid="ca-new-submit"
                                className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md">Create</button>
                        </div>
                    </div>
                </div>
            )}
            {editingRow && (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40" data-testid="ca-edit-due-modal">
                    <div className="bg-white rounded-lg p-4 w-[420px] flex flex-col gap-3">
                        <div className="font-manrope font-semibold">Revise due date · {editingRow.title}</div>
                        <label className="flex flex-col gap-1">
                            <span className="text-[11px] uppercase font-manrope text-[#666]">New due date &amp; time</span>
                            <input type="datetime-local" value={editDueAt}
                                onChange={e => setEditDueAt(e.target.value)}
                                data-testid="ca-edit-due-input"
                                className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                        </label>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => { setEditingRow(null); setEditDueAt(""); }} className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md">Cancel</button>
                            <button onClick={submitDueAtEdit} data-testid="ca-edit-due-submit"
                                className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md">Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function ProjectAttention() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    usePageMeta({ title: "Attention · Project | Studio G4 Admin" });
    const load = () => api.get(`/admin/projects/${id}/attention`).then(r => setRows(r.data?.items || []));
    useEffect(() => { load(); }, [id]);
    const resolve = async (r) => {
        if (!r.resolvable_generically) return;
        try {
            await api.post(`/admin/attention/${r.id}/resolve`,
                { expected_version: r.record_version, note: "Resolved via workspace" });
            load();
        } catch (e) { alert("Resolve blocked: " + (e?.response?.data?.detail || e.message)); }
    };
    return (
        <div className="flex flex-col gap-4" data-testid="attention-page">
            <button onClick={() => navigate(`/admin/projects/${id}`)}
                data-testid="attention-back" className="self-start inline-flex items-center gap-1.5 text-[13px] text-[#666] hover:text-charcoal">
                <ArrowLeft className="w-4 h-4" /> Back to workspace
            </button>
            <h1 className="font-manrope text-2xl font-semibold text-charcoal">Attention</h1>
            {rows.length === 0 && <div className="text-[13px] text-[#666]">No unresolved attention.</div>}
            <div className="flex flex-col gap-2">
                {rows.map(r => (
                    <Row key={r.id} testid={`attn-row-${r.id}`}>
                        <AlertTriangle className="w-4 h-4 text-terracotta shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <div className="font-manrope font-semibold text-[14px]">{r.title}</div>
                            <div className="text-[12px] text-[#666]">
                                source {r.source_type} · resolution_mode {r.resolution_mode || <span className="text-terracotta">unknown (fails closed)</span>}
                                {r.source_authority ? ` · owned by ${r.source_authority}` : ""}
                            </div>
                        </div>
                        {r.resolvable_generically ? (
                            <button onClick={() => resolve(r)} data-testid={`attn-resolve-${r.id}`}
                                className="text-[12px] px-2.5 py-1 bg-charcoal text-white rounded-md inline-flex items-center gap-1">
                                <Check className="w-3 h-3" /> Resolve
                            </button>
                        ) : (
                            <span className="text-[11px] text-[#666] italic px-2.5 py-1" data-testid={`attn-workflow-only-${r.id}`}>
                                Resolve via owning workflow
                            </span>
                        )}
                    </Row>
                ))}
            </div>
        </div>
    );
}
